import { useMsal } from "@azure/msal-react";
import { DefaultButton, Dialog, DialogFooter, DialogType, mergeStyleSets, Text, useTheme } from "@fluentui/react";
import { IExceptionTelemetry } from "@microsoft/applicationinsights-web";
import { RootState } from "app/store";
import { EValidationSeverity, IValidationMessage } from "components/controls/ValidationView/ValidationView.type";
import { saveAs } from "file-saver";
import { emitCustomEvent } from "global/Application";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import PageLoading from "global/PageLoading";
import { IGCDataLight } from "global/Reducer/dataSlice";
import { ELoadStatus } from "global/Reducer/statusSlice";
import { ValidateDataAsync } from "global/Validation/DataValidation";
import { EDataValidationContext } from "global/Validation/EDataValicationContext";
import { bcbs_groupconfigurationsnapshot } from "model/AttributeOptionSets.type";
import { ChangeTypePermissions, IChangeTypePermission } from "model/ChangeTypePermissions";
import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from 'uuid';
import { ICompareConfiguration } from "views/BlueRelationship/CompareLookupDialog/CompareLookupDialog";
import ValidationDialog from "views/BlueRelationship/ValidationDialog";
import { EValidationState, IValidationModel } from "views/BlueRelationship/ValidationDialog/ValidationDialog.type";
import { EViewType, panelButtonsStyles, subscribeCustomEvent } from ".";
import { appInsights } from "./AppInsight";
import { ESaveType, ISaveInstruction, SaveAction } from "./dataActions";

export type TReactState<S> = [S, Dispatch<SetStateAction<S | null>>];

export interface IComparisonStateData {
    origin: IGCDataLight | null;
    target: IGCDataLight | null;
    configuration: ICompareConfiguration;
}


export interface IApplicationContext {
    events: any | null;
    dataSource: AppDataSource;
    QueueError: (error: IError) => void;
    SaveAsync: (baseInstructions: ISaveInstruction) => void;
    ChangeTypePermissions: IChangeTypePermission;
    comparisonState: TReactState<IComparisonStateData | null>;
}


interface IError {
    telemetry?: IExceptionTelemetry;
    description?: string;
    error?: Error | any;
    timeStamp?: string;
    location?: string;
}




const defaultContext: IApplicationContext = {
    events: null,
    dataSource: null as any,
    QueueError: (state) => { },
    SaveAsync: (baseInstructions) => { },
    ChangeTypePermissions: {} as any,
    comparisonState: null as any,
};

export const ApplicationContext = createContext(defaultContext);

const ApplicationContextProvider = (props: { children: React.ReactNode }) => {

    const theme = useTheme();
    const history = useHistory();

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const [errorQueue, setErrorQueue] = useState<IError[]>([]);

    const state = useSelector((state: RootState) => state);

    const comparisonState = useState<IComparisonStateData | null>(null);

    // multiple sources can change state, to avoid race, ref is the latest one always.
    // for ui bindings, adn react refresh, validationState can be used
    const validation = useRef<IValidationModel<ISaveInstruction> | null>(null);
    const [validationState, setValidationState] = useState<EValidationState | null>(null);

    const dispatch = useDispatch();

    const appInsightLog = (error: any) => {

        // appInsights not setup?
        if (!appInsights)
            return;

        const telemetry: IExceptionTelemetry = {
            id: uuidv4(),
        };

        if (error instanceof Error) {
            telemetry.exception = error;

            telemetry.properties = {
                ...telemetry.properties,
                message: error.message,
                id: telemetry.id,
            };
        }
        else {
            const errorEvent = error as ErrorEvent;

            telemetry.exception = errorEvent?.error;

            telemetry.properties = {
                ...telemetry.properties,
                message: errorEvent?.message,
                id: telemetry.id,
            };
        }

        appInsights.trackException(telemetry);
        appInsights.flush();

        return telemetry;
    }

    //globalException handler
    useEffect(() => {

        const onErrorHandler = (event: ErrorEvent) => {

            // BCBSSC regular users have an extension that causes an exception:
            // before the code manages to finish the service cycle, another one is queued or ignored.
            // ResizeObserver loop completed with undelivered notifications.
            // ResizeObserver loop limit exceeded
            if (event.message.includes('ResizeObserver loop')) {
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }

            console.error(event);

            const telemetry = appInsightLog(event);

            setErrorQueue(t => {
                const _t = [...t];
                _t.push({
                    error: JSON.parse(JSON.stringify(event, Object.getOwnPropertyNames(event))),
                    telemetry: telemetry,
                    timeStamp: (new Date()).toISOString(),
                    location: window.location.href,
                });
                return _t;
            });
        }

        const onServiceWorkerMessage = (event: MessageEvent) => {
            if (event.data.type === 'cache-updated') {
                const isBCCAdmin = event.data?.type === "https://bcbsonlinesys.crm.dynamics.com/api/data/v9.1/bcbs_bccadmins"
            }
            console.log("message", event);
        }

        window.addEventListener('error', onErrorHandler);
        navigator.serviceWorker.addEventListener("message", onServiceWorkerMessage);

        const documentRemoveEventListener = subscribeCustomEvent({
            eventType: 'globalException',
            handler: (event: any) => {
                console.error(event);

                const telemetry = appInsightLog(event.detail);

                setErrorQueue(t => {
                    const _t = [...t];
                    _t.push({
                        error: event?.detail
                            ? JSON.parse(JSON.stringify(event.detail, Object.getOwnPropertyNames(event.detail)))
                            : undefined,
                        telemetry: telemetry,
                        timeStamp: (new Date()).toISOString()
                    });
                    return _t;
                });
            },
        });

        return () => {
            window.removeEventListener('error', onErrorHandler);
            navigator.serviceWorker.removeEventListener("message", onServiceWorkerMessage);
            documentRemoveEventListener();
        }

    }, []);

    // useEffect(() => {
    //     // const myWorker = new Worker(new URL('src/worker', import.meta.url));
    //     // const myWorker = new Worker("/emitEventWorker.ts");
    //     const myWorker = new Worker(worker_script);

    //     myWorker.onmessage = (m) => {
    //         console.log("msg from worker: ", m.data);
    //     };
    //     myWorker.postMessage('im from main');

    //     return () => {
    //         myWorker.terminate();
    //     }
    // }, []);

    const SaveAsync = async (xInstructions?: ISaveInstruction) => {

        try {

            // reload associated opportunity in case it had any other changes
            let isGoldStandard = state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value;
            let isRetired = state.data.snapshot.statuscode === bcbs_groupconfigurationsnapshot.statuscode['Retired(808900001)'].value;

            // any other save requests, while we are verifying, ignore
            if (validation.current?.state === EValidationState.Validating || validation.current?.state === EValidationState.PendingUser) {
                console.log('ignoring save request, validation is running.', validation.current);
                return;
            }

            // context has no validation attached? reset everything
            // rejected? reset everything
            if (!validation.current || validation.current.state === EValidationState.Rejected)
                validation.current = {
                    state: EValidationState.Idle,
                    data: xInstructions ?? {},
                    validationMessages: [],
                }

            const saveInstructions = validation.current.data!;

            if (state.status.debug.clone)
                saveInstructions.onSaveClone = true;

            const validationMessages = validation.current.validationMessages;

            if (validation.current.state === EValidationState.Idle) {
                validation.current.state = EValidationState.Validating;
            }

            // adjusting save instructions based on feedback if any:
            // this is used on second round coming over again
            if (validation.current.state === EValidationState.Fulfilled) {
                validation.current.validationMessages.forEach(eachVM => {

                    if (eachVM.id === 'gold-standard-prompt') {

                        const goldStandardPrompt = eachVM as IValidationMessage;

                        if (goldStandardPrompt?.value === "yes") {
                            saveInstructions.onSaveGenerateGoldStandardAndSQL = true;
                        }
                    }
                    else if (eachVM.id === 'update-release-prompt') {

                        const releasePrompt = eachVM as IValidationMessage;

                        if (releasePrompt?.value === "yes") {
                            saveInstructions.onSaveUpdateRelease = true;
                        }
                    }
                    else if (eachVM.id === 'force-in-progress') {

                        const releasePrompt = eachVM as IValidationMessage;

                        if (releasePrompt?.value === "yes") {
                            saveInstructions.onSaveForceStatusCodeToInProgress = true;
                        }
                    }
                });

            }

            // Not sure what else might come up. use case areas are very blurry
            // I don't really have a design for this, jerry rigging this until full use case is clear
            // and we can implement a proper validation system, what I know so far:
            // - Validation on submit, for required fields for given change type
            // - PSA + Underwriting submission needs to be rerouted through validation
            // - Underwriting (review complete) collision with other snapshots
            // - cancellation pending status to chance to committed cancellation status 
            // - cancellation fields to be populated (unconfirmed)

            // Pending:
            // - coverage level
            // - cancellation required levels and fields
            // - field validation
            // - duplicate names on entities like plans, benefits, ...

            if (validation.current.state === EValidationState.Validating) {
                if (saveInstructions.saveType === ESaveType.ESubmit) {

                    setValidationState(validation.current!.state);

                    // Data Validation
                    // ----------------------------------------------------------------------------------------------
                    // *** saveInstructions does get updated
                    // TODO return new instructions, making parameter immutable
                    const dataValidationResult = await ValidateDataAsync(
                        state,
                        {
                            checkRef: true,
                            checkUniqueName: true,
                            checkCoverage: true,
                            checkGrandfathered: true,
                            checkPlanGroupStructure: true,
                            checkBillingOnlyPlan: true,
                            checkRelease: true,
                            checkRequiredFields: true,
                            checkData: true,
                        },
                        EDataValidationContext.ESaveSubmit,
                        history,
                        saveInstructions,
                        dataSource,
                    );

                    if (dataValidationResult.refValidations.length > 0) {
                        validationMessages.push({
                            title: `Some elements are referencing a resource that has been deactivated or no longer exists.`,
                            description: `The occurrence of such a situation may arise when a resource undergoes deactivation during the course of business processing or fails to be selected for retention in the subsequent iteration of the merge process. In such cases, it is imperative to reassign or clear each individual entry.`,
                            type: EValidationSeverity.error,
                            inner: dataValidationResult.refValidations
                        });
                    }

                    if (dataValidationResult.uniqueEntityNameValidations.length > 0) {
                        const t = {
                            title: `Some elements are sharing an identifier within the same context.`,
                            description: `The following elements have been flagged due to naming errors, necessitating the assignment of unique names. To make the necessary changes, please navigate to the Properties section.\nHere is the list of impacted elements:`,
                            type: EValidationSeverity.info,
                            inner: dataValidationResult.uniqueEntityNameValidations
                        };

                        validationMessages.push(t);

                        if (dataValidationResult.uniqueEntityNameValidations.some(t => t.type === EValidationSeverity.error)) {
                            t.type = EValidationSeverity.error;
                        }
                    }

                    if (dataValidationResult.coverageValidations.length > 0) {
                        validationMessages.push({
                            title: 'Coverage mismatch has been detected.',
                            type: EValidationSeverity.error,
                            inner: dataValidationResult.coverageValidations
                        });
                    }

                    if (dataValidationResult.grandFatherValidations.length > 0) {
                        validationMessages.push({
                            title: 'Grandfathered/Non-Grandfathered mismatch has been detected.',
                            type: EValidationSeverity.error,
                            inner: dataValidationResult.grandFatherValidations
                        });
                    }

                    if (dataValidationResult.validationMessages.length > 0)
                        validationMessages.push(...dataValidationResult.validationMessages);

                    if (dataValidationResult.planGroupStructureValidations.length > 0)
                        validationMessages.push(...dataValidationResult.planGroupStructureValidations);

                    if (dataValidationResult.billingOnlyPlanValidations.length > 0)
                        validationMessages.push(...dataValidationResult.billingOnlyPlanValidations);

                    if (dataValidationResult.releaseValidations)
                        validationMessages.push(...dataValidationResult.releaseValidations);

                    if (dataValidationResult.relatedToGroupNumberValidations)
                        validationMessages.push(...dataValidationResult.relatedToGroupNumberValidations);

                    if (dataValidationResult.requiredFieldsValidations)
                        validationMessages.push(...dataValidationResult.requiredFieldsValidations);

                    if (dataValidationResult.dataValidations)
                        validationMessages.push(...dataValidationResult.dataValidations);

                }
            }

            // TODO add retired here to be able to regen 
            // Gold Standard Modification Prompt
            if (validation.current.state === EValidationState.Validating)
                if (isGoldStandard || isRetired) {

                    // TODO id-message tracking for azure logs
                    const message: IValidationMessage = {
                        id: `gold-standard-prompt`,
                        title: `Update related data and processes.`,
                        description: `Modifying a gold standard/retired record directly will bypass data generation.\nEnable this option to reflect latest changes, update reports, and potentially trigger down stream processes.`,
                        type: EValidationSeverity.promptBoolean,
                    };

                    validationMessages.push(message);
                }

            // Admin Mode Prompt
            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.admin)
                    validationMessages.push({
                        title: `(Admin) mode is on.`,
                        description: `When using admin mode you can continue with saving the data.\nValidations can not prevent bad data submission.`,
                        type: EValidationSeverity.warn,
                    });

            // Force Update Release
            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.admin) {

                    // TODO id-message tracking for azure logs
                    const t: IValidationMessage = {
                        id: `update-release-prompt`,
                        title: `(Admin) Update release information on snapshot`,
                        description: `checking this will update the referenced release record to the latest published version.`,
                        type: EValidationSeverity.promptBoolean,
                    };

                    validationMessages.push(t);
                }

            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.admin) {

                    // TODO id-message tracking for azure logs
                    const t: IValidationMessage = {
                        id: `force-in-progress`,
                        title: `(Admin) Reopen as in-progress`,
                        description: `Reopen as in-progress on save`,
                        type: EValidationSeverity.promptBoolean,
                    };

                    validationMessages.push(t);
                }

            // Live Load Check
            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.liveload)
                    validationMessages.push({
                        title: `Live Configuration Loading - Ensure Accuracy of Updates.`,
                        description: `You are currently live loading the configuration.\nPlease note that saving data at this point may result in unpredictable behavior. To ensure your updates are applied correctly, we recommend reloading the page`,
                        type: EValidationSeverity.error,
                    });


            // context visibility notification, to make sure user know changed data elements are not reflected
            if (validation.current.state === EValidationState.Validating)
                if (state.status.debug.noVisibility)
                    validationMessages.push({
                        title: `Context visibility has been ignored.`,
                        type: EValidationSeverity.error,
                    });


            // Sorting
            // ---------------------------------
            validationMessages.sort((b, a) => { return b.type - a.type });

            // Results
            // ---------------------------------
            if (validation.current.validationMessages.some(q => !q.resolved))
                validation.current.state = EValidationState.PendingUser;
            else if (validation.current.state !== EValidationState.Rejected)
                validation.current.state = EValidationState.Fulfilled;

            setValidationState(validation.current!.state);

            // validation has been complete or non has been performed:
            if (validation.current.state === EValidationState.Fulfilled) {
                dispatch(
                    SaveAction(
                        dataSource,
                        history,
                        saveInstructions
                    )
                );
            }

            // if final state, reset everything
            if (validation.current.state === EValidationState.Fulfilled || validation.current?.state === EValidationState.Rejected)
                validation.current = null;

        } catch (error: any) {
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });
            console.error(error);

            setValidationState(null);
            validation.current = null;
        } finally {

        }
    }

    const onQueueError = (item: IError) => {
        setErrorQueue(t => {
            const _t = [...t];
            _t.push(item);
            return _t;
        });
    }

    const context: IApplicationContext = {
        ...defaultContext,
        dataSource: dataSource,
        QueueError: onQueueError,
        SaveAsync: SaveAsync,
        ChangeTypePermissions: ChangeTypePermissions[0],
        comparisonState: comparisonState,
    };

    if (state?.data?.json?.changeType?.value! && ChangeTypePermissions[state.data.json.changeType.value])
        context.ChangeTypePermissions = ChangeTypePermissions[state.data.json.changeType.value]

    // admin override
    if (state.status.debug.admin)
        context.ChangeTypePermissions = ChangeTypePermissions[1];

    // ctrl+s save
    useEffect(() => {

        const documentOnKeyDown = (e: any) => {
            if ((window.navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey) && e.keyCode === 83) {
                // to prevent browsers default action for page save
                e.preventDefault();

                // focus out, giving slice for data to commit
                if (document.activeElement)
                    //@ts-ignore
                    document.activeElement?.blur?.();
            }
        };

        document.addEventListener("keydown", documentOnKeyDown, false);

        const documentOnKeyUp = (event: KeyboardEvent) => {

            if ((window.navigator.platform.match("Mac") ? event.metaKey : event.ctrlKey) && event.keyCode === 83) {
                event.preventDefault();

                // if already saving ?
                if (state.status.dataSavingStatus === ELoadStatus.criticalSaving)
                    return;

                console.log('context, servicing shortcut (ctrl+s)');
                setTimeout(() => {
                    SaveAsync({ saveType: ESaveType.EAuto });
                }, 100);
            };
        }

        document.addEventListener("keyup", documentOnKeyUp, false);


        return () => {
            // clear (CTRL + S)
            document.removeEventListener("keyup", documentOnKeyUp);
            document.removeEventListener("keydown", documentOnKeyDown);
        }

    }, []);

    const windowBeforeUnload = useCallback((event: BeforeUnloadEvent) => {

        let eject = false;

        // if not modifying ignore this check
        if (state.status.viewType !== EViewType.Modify)
            eject = true;

        // const saveTokenRef = context.saveInfo.saveTokenMut
        //     ? context.saveInfo.saveTokenMut()
        //     : undefined;

        // const storeTokenRef = context.saveInfo.storeTokenMut
        //     ? context.saveInfo.storeTokenMut()
        //     : undefined;

        // if ((saveTokenRef?.current ?? 0) > (storeTokenRef?.current ?? 0))
        //     return;

        if ((state.data.savedOn ?? 0) > (state.data.modifiedOn ?? -1)) {
            // dirty changes?
            eject = true;
        }

        if (state.status.dataSavingStatus !== ELoadStatus.idle && state.status.dataSavingStatus !== ELoadStatus.complete)
            eject = false;

        if (!eject) {
            event.preventDefault();
            event.returnValue = "Are you sure you want to quit?"
            return event.returnValue;
        }
    }, [state]);

    // unload event
    useEffect(() => {
        window.addEventListener('beforeunload', windowBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', windowBeforeUnload);
        }
    }, [windowBeforeUnload]);

    // auto save
    useEffect(() => {
        const MINUTE_MS = 5 * 60 * 1000;

        const interval = setInterval(() => {

            if (state.status.viewType !== EViewType.Modify) {
                console.log('[appl] Context.interval', "autosave-eject-viewOnly");
                return;
            }

            if (state?.status?.debug?.autoSaveOff) {
                console.log('[appl] Context.interval', "autosave-eject-debug");
                return;
            }
            // if already saving ?
            if (state.status.dataSavingStatus !== ELoadStatus.idle) {
                console.log('[appl] Context.interval', "autosave-eject-pending");
                return;
            }

            // TODO add a check for last save
            console.log('[appl] Context.interval', "autosave");
            SaveAsync({ saveType: ESaveType.EAuto });

        }, MINUTE_MS);

        return () => {
            clearInterval(interval);
        }
    }, []);

    const contentStyles = useMemo(() => {
        return mergeStyleSets({
            container: {
                width: '600px',
                minWidth: '600px'
            }
        });
    }, []);

    const lastError = errorQueue?.[0];

    const onRenderErrorDialog = () => {
        return <Dialog
            minWidth={600}
            hidden={errorQueue.length === 0}
            onDismiss={() => setErrorQueue([])}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: 'Uh oh! Something went wrong...',
                subText: `Try this action again. If the problem continues, contact your organization's Group Library Administrator.`,
            }}
            modalProps={{
                isBlocking: false,
                containerClassName: contentStyles.container
            }}
        >
            <>
                {lastError?.error?.description &&
                    <>
                        <Text>{lastError.error.description}</Text>
                        <br />
                    </>
                }

                {lastError && lastError.error &&
                    <div style={{
                        boxSizing: 'border-box',
                        backgroundColor: theme.semanticColors.bodyBackground,
                        border: '1px solid rgb(202, 202, 202)',
                        width: '100%',
                        fontSize: 'x-small',
                        maxHeight: '12em',
                        overflowY: 'scroll',
                        whiteSpace: 'pre-wrap',
                        color: theme.semanticColors.bodyText,
                    }}>
                        <code>

                            {lastError.timeStamp &&
                                <div>Timestamp: {lastError.timeStamp}</div>
                            }

                            {lastError.telemetry?.id &&
                                <div>Insight Tracker: {lastError.telemetry.id}</div>
                            }

                            {!lastError.description &&
                                <p>
                                    {lastError.description}
                                </p>
                            }
                            <div>

                                {lastError.error.message &&
                                    <div>
                                        {lastError.error.message}
                                    </div>
                                }
                                {lastError.error.stack &&
                                    <div>
                                        {lastError.error.stack}
                                    </div>
                                }
                            </div>
                        </code>
                    </div>
                }
            </>


            <DialogFooter>
                <DefaultButton text="Download Log"
                    styles={panelButtonsStyles}
                    onClick={() => {
                        const log = JSON.stringify(errorQueue);
                        // const subject = `Group Library - Exception`;
                        // const body = `Regarding: ${window.location.href}\nWhat I was doing:\n[describe steps to reproduce the issue]\nStack:` + log;
                        // window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                        const blob = new Blob([log], { type: 'text/plan;charset=utf-8' });
                        saveAs(blob, 'log.txt', { autoBom: true });
                    }}
                />
                <DefaultButton text="OK"
                    styles={panelButtonsStyles}
                    onClick={() => {
                        setErrorQueue(t => {
                            const _t = [...t];

                            // remove first item on the queue;
                            _t.shift();

                            return _t;
                        });
                    }}
                />
            </DialogFooter>
        </Dialog>
    }

    const onRenderValidationDialog = () => {
        return <>
            {validationState && (validationState === EValidationState.Validating || validationState === EValidationState.PendingUser) &&
                <ValidationDialog
                    defaultValue={validation.current!}
                    loadingPageDuringValidation={true}
                    onCancel={() => {
                        validation.current!.state = EValidationState.Rejected;
                        setValidationState(validation.current!.state);
                    }}
                    onContinue={(e: IValidationModel) => {
                        validation.current = e;
                        validation.current!.state = EValidationState.Fulfilled;

                        validation.current.validationMessages.forEach(vm => {
                            vm.resolved = true;
                        });

                        SaveAsync(validation.current.data);
                    }}
                />
            }
        </>;
    }

    if (state.status.recycling)
        return <PageLoading />

    return (
        <ApplicationContext.Provider value={context}>
            {props.children}
            {onRenderValidationDialog()}
            {onRenderErrorDialog()}
        </ApplicationContext.Provider>
    );
}

export default ApplicationContextProvider;
export const useApplication = () => useContext(ApplicationContext);
